import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Text, View, useWindowDimensions} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';
import i18n from 'i18n-js';
import * as Clipboard from 'expo-clipboard';
import ServerPaginatedDataGrid from '../../commons/ServerPaginatedDataGrid';
import useDebouncedSearch from '../../commons/ServerPaginatedDataGrid/hooks/useDebouncedSearch';
import Toast from '../../commons/Toast';
import {
  deleteImportSummary,
  deleteTenant,
  duplicateTenant,
  fetchTenantColumnPrefs,
  fetchTenants,
  restoreTenant,
  saveTenantColumnPrefs,
  setTestTenant
} from '../../actions/adminTenantsActions';
import {fetchUserInfo} from '../../actions/userAction';
import {fontFamily} from '../../helpers/fontFamily';
import {
  accentColor,
  breakpointPhone,
  dangerColor,
  groupBg,
  groupBorderColor,
  groupRadius,
  textColor,
  textMutedColor
} from '../../style/adminTools';
import AdminPanelShell from './tenantGrid/AdminPanelShell';
import Pill from './tenantGrid/Pill';
import GridToolbar from './tenantGrid/GridToolbar';
import {
  EmptySearchPanel,
  FailureBanner,
  FailurePanel,
  NoResultsPanel
} from './tenantGrid/GridStateBanner';
import ShimmerRows from './tenantGrid/ShimmerRows';
import TenantActionDialog from './tenantGrid/TenantActionDialog';
import TenantPhone from './tenantGrid/TenantPhone';
import {DEFAULT_SORT_KEY} from './tenantGrid/phoneSortOptions';
import TenantPager, {ROWS_PER_PAGE} from './tenantGrid/TenantPager';
import {
  buildTenantColumns,
  columnLabels,
  DEFAULT_HIDDEN_COLUMNS,
  formatClock,
  hiddenFromVisible,
  sortParam,
  visibleFromHidden
} from './tenantGrid/TenantCells';

const t = (key, options) => i18n.t(`adminTenantGrid.${key}`, options);

const noop = () => {};

const TENANT_GRID_ID = 'gpx_tenants';

const SUCCESS_TOAST_MS = 4000;
const PHONE_TOAST_BOTTOM = 140;

const attempt = async work => {
  try {
    return await work();
  } catch (error) {
    return {ok: false, error: error.message};
  }
};

const rowStyle = ({data}) => (data.is_deleted ? {opacity: 0.6} : undefined);

const renderPager = paginationProps => <TenantPager {...paginationProps} />;

const PILL_GAP = {marginLeft: 8};

const PagerPlaceholder = () => (
  <View
    testID="tenantSkeletonPager"
    accessibilityState={{disabled: true}}
    style={{
      height: 60,
      borderRadius: groupRadius,
      borderWidth: 1,
      borderColor: groupBorderColor,
      backgroundColor: groupBg
    }}
  />
);

const DIALOGS = {
  delete: {
    message: row => t('deleteConfirm', {name: row.name}),
    label: () => t('delete'),
    danger: true
  },
  duplicate: {
    message: row => t('duplicatePrompt', {name: row.name}),
    label: () => t('duplicate'),
    withInput: true,
    inputMatches: () => value => value.trim() !== ''
  },
  restore: {
    message: () => t('restoreConfirm'),
    label: () => t('restore'),
    danger: true,
    withInput: true,
    inputMatches: row => value => value === row.name
  },
  deleteSummary: {
    message: row => t('deleteSummaryConfirm', {name: row.name}),
    label: () => t('delete'),
    danger: true
  }
};

const TenantGrid = ({onOpenMenu, onOpenTenant = noop, gridProps}) => {
  const dispatch = useDispatch();
  const {
    counts,
    lastLoadedAt,
    status: loadStatus
  } = useSelector(state => state.adminTenants);
  const userInfo = useSelector(state => state.user?.userInfo);
  const {width} = useWindowDimensions();
  const isPhone = width < breakpointPhone;

  const {searchValue, debouncedValue, handleSearchChange, clearSearch} =
    useDebouncedSearch('', 500);
  const [status, setStatus] = useState('active');
  const [allSorting, setAllSorting] = useState(false);
  const [sortKey, setSortKey] = useState(DEFAULT_SORT_KEY);
  const [hiddenColumns, setHiddenColumns] = useState(DEFAULT_HIDDEN_COLUMNS);
  const [columnPrefsStatus, setColumnPrefsStatus] = useState('pending');
  const columnSaves = useRef(Promise.resolve());
  const [refreshKey, setRefreshKey] = useState(0);
  const [failure, setFailure] = useState(null);
  const [total, setTotal] = useState(null);
  const [rowsState, setRowsState] = useState({key: null, has: false});
  const [selection, setSelection] = useState({rows: [], count: 0});
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const query = debouncedValue.trim();
  const requestRef = useRef({});
  const gridKey = `${debouncedValue}|${status}|${allSorting}`;
  const [skipState, setSkipState] = useState({key: gridKey, skip: 0});
  if (skipState.key !== gridKey) setSkipState({key: gridKey, skip: 0});
  const gridSkip = skipState.key === gridKey ? skipState.skip : 0;
  requestRef.current = {query: debouncedValue, status, allSorting, gridKey};

  const clearNotice = useCallback(() => setNotice(null), []);

  const noticeId = notice ? notice.id : null;
  const noticeIsError = Boolean(notice && notice.tone === 'error');
  useEffect(() => {
    if (noticeId === null || noticeIsError) return undefined;
    const timer = setTimeout(clearNotice, SUCCESS_TOAST_MS);
    return () => clearTimeout(timer);
  }, [noticeId, noticeIsError, clearNotice]);

  const notify = useCallback(
    (message, tone) => setNotice({message, tone, id: Date.now()}),
    []
  );

  const fetchData = useCallback(
    async ({skip, limit, sortInfo}) => {
      setFailure(null);
      const request = requestRef.current;
      const result = await dispatch(
        fetchTenants({
          skip,
          limit,
          sortInfo: sortInfo && {...sortInfo, name: sortParam(sortInfo.name)},
          searchQuery: request.query,
          status: request.status,
          allTenantSorting: request.allSorting
        })
      );
      if (!result.stale) {
        setTotal(result.count);
        setRowsState({key: request.gridKey, has: result.data.length > 0});
      }
      return result;
    },
    [dispatch]
  );

  const fetchDesktopPage = useCallback(
    async args => {
      const {gridKey: requestKey} = requestRef.current;
      const result = await fetchData(args);
      if (!result.stale && result.data.length === 0 && args.skip > 0) {
        setSkipState({
          key: requestKey,
          skip: Math.max(0, args.skip - args.limit)
        });
      }
      return result;
    },
    [fetchData]
  );

  const handleFetchError = useCallback(error => {
    setRowsState({key: null, has: false});
    setFailure({
      kind: error.kind || null,
      status: error.status === undefined ? null : error.status,
      message: error.message
    });
  }, []);

  const retry = useCallback(() => setRefreshKey(key => key + 1), []);

  const copyErrorDetails = useCallback(async () => {
    const outcome = await attempt(async () => {
      await Clipboard.setStringAsync(
        JSON.stringify({
          ...failure,
          lastLoadedAt: lastLoadedAt
            ? new Date(lastLoadedAt).toISOString()
            : null
        })
      );
      return {ok: true};
    });
    if (!outcome.ok) notify(outcome.error, 'error');
  }, [failure, lastLoadedAt, notify]);

  const userInfoRequested = useRef(false);
  useEffect(() => {
    if (userInfo || userInfoRequested.current) return;
    userInfoRequested.current = true;
    dispatch(fetchUserInfo());
  }, [dispatch, userInfo]);

  // Without both fields gridColumnPreferences falls back to one key shared by every user.
  const prefsUser =
    userInfo?.current_tenant && userInfo?.username ? userInfo : null;

  useEffect(() => {
    if (isPhone) return undefined;
    let ignore = false;
    dispatch(fetchTenantColumnPrefs()).then(result => {
      if (ignore) return;
      if (!result.ok) {
        setColumnPrefsStatus('failed');
        notify(t('columnPrefsLoadFailed'), 'error');
        return;
      }
      if (result.visible) setHiddenColumns(hiddenFromVisible(result.visible));
      setColumnPrefsStatus('loaded');
    });
    return () => {
      ignore = true;
    };
  }, [dispatch, isPhone, notify]);

  const openDialog = useCallback((kind, row) => setDialog({kind, row}), []);
  const handleToggleTestTenant = useCallback(
    async (row, next) => {
      const result = await attempt(() => dispatch(setTestTenant(row, next)));
      if (!result.ok) notify(result.error, 'error');
      return result;
    },
    [dispatch, notify]
  );

  const labels = useMemo(() => columnLabels(), []);

  const columns = useMemo(
    () =>
      buildTenantColumns({
        hidden: hiddenColumns,
        onOpenTenant,
        onToggleTestTenant: handleToggleTestTenant
      }),
    [hiddenColumns, onOpenTenant, handleToggleTestTenant]
  );

  const confirmDialog = async value => {
    const {kind, row} = dialog;
    const thunks = {
      delete: () => deleteTenant(row),
      duplicate: () => duplicateTenant(row, value.trim()),
      restore: () => restoreTenant(row),
      deleteSummary: () => deleteImportSummary(row)
    };
    setBusy(true);
    const result = await attempt(() => dispatch(thunks[kind]()));
    setBusy(false);
    setDialog(null);
    if (!result.ok) {
      notify(result.error, 'error');
      return;
    }
    if (result.message) notify(result.message, 'success');
    if (kind !== 'deleteSummary') retry();
  };

  const toggleColumn = useCallback(
    name => {
      const next = {...hiddenColumns, [name]: !hiddenColumns[name]};
      setHiddenColumns(next);
      if (columnPrefsStatus !== 'loaded') return;
      const visible = visibleFromHidden(next);
      columnSaves.current = columnSaves.current
        .then(() => dispatch(saveTenantColumnPrefs(visible)))
        .then(result => {
          if (!result.ok)
            notify(t('columnPrefsSaveFailed', {error: result.error}), 'error');
        });
    },
    [columnPrefsStatus, dispatch, hiddenColumns, notify]
  );

  const handleRows = useCallback(
    (rows, meta) => setSelection({rows, count: meta.count}),
    []
  );

  const handleSkipChange = useCallback(
    skip => setSkipState({key: requestRef.current.gridKey, skip}),
    []
  );

  const handlePhoneSelection = useCallback(
    rows => setSelection({rows, count: rows.length}),
    []
  );

  const applyPhoneFilters = useCallback(next => {
    setStatus(next.status);
    setSortKey(next.sortKey);
    setAllSorting(next.allSorting);
  }, []);

  const onlyRow =
    selection.count === 1 && selection.rows.length === 1
      ? selection.rows[0]
      : null;
  const reasonFor = key =>
    selection.count > 1 ? t('oneAtATimeTooltip') : t(key);
  const actions = {
    duplicate: {
      enabled: Boolean(onlyRow && !onlyRow.is_deleted),
      reason: reasonFor('duplicateDisabledTooltip'),
      onPress: () => openDialog('duplicate', onlyRow)
    },
    delete: {
      enabled: Boolean(onlyRow && !onlyRow.is_deleted),
      reason: reasonFor('deleteDisabledTooltip'),
      onPress: () => openDialog('delete', onlyRow)
    },
    restore: {
      enabled: Boolean(onlyRow && onlyRow.is_deleted),
      reason: reasonFor('restoreDisabledTooltip'),
      onPress: () => openDialog('restore', onlyRow)
    },
    deleteSummary: {
      enabled: Boolean(onlyRow),
      reason: reasonFor('deleteSummaryDisabledTooltip'),
      onPress: () => openDialog('deleteSummary', onlyRow)
    }
  };

  const allCount = (counts && counts.all) || total || 0;
  const loading = loadStatus === 'pending';
  const rowsOnScreen = rowsState.has && rowsState.key === gridKey;

  let subline;
  if (failure) subline = t('couldNotLoad');
  else if (loading) subline = t('loading');
  else if (query) subline = t('matchCount', {count: total || 0});
  else subline = t('tenantCount', {count: allCount});

  const emptyContent = query ? (
    <EmptySearchPanel query={query} count={allCount} onClear={clearSearch} />
  ) : (
    <NoResultsPanel />
  );

  const dialogConfig = dialog ? DIALOGS[dialog.kind] : null;

  const dialogView = dialog ? (
    <TenantActionDialog
      sheet={isPhone}
      message={dialogConfig.message(dialog.row)}
      confirmLabel={dialogConfig.label()}
      danger={dialogConfig.danger}
      withInput={dialogConfig.withInput}
      inputMatches={
        dialogConfig.withInput
          ? dialogConfig.inputMatches(dialog.row)
          : undefined
      }
      busy={busy}
      onConfirm={confirmDialog}
      onCancel={() => setDialog(null)}
    />
  ) : null;

  const toastView = (
    <Toast
      key={notice ? notice.id : 'none'}
      visible={Boolean(notice)}
      message={notice ? notice.message : ''}
      persistent
      noModal
      onHide={clearNotice}
      onClose={noticeIsError ? clearNotice : undefined}
      bottomOffset={isPhone ? PHONE_TOAST_BOTTOM : null}
      textColor={textColor}
      colors={
        noticeIsError ? [dangerColor, dangerColor] : [accentColor, accentColor]
      }
    />
  );

  if (isPhone) {
    return (
      <>
        <TenantPhone
          onOpenMenu={onOpenMenu}
          onOpenTenant={onOpenTenant}
          search={{
            searchValue,
            debouncedValue,
            handleSearchChange,
            clearSearch
          }}
          filters={{status, sortKey, allSorting}}
          onFiltersChange={applyPhoneFilters}
          grid={{fetchData, onFetchError: handleFetchError, refreshKey}}
          loading={loading}
          failure={failure}
          failureView={{
            statusText: failure
              ? String(
                  failure.status === null ? failure.message : failure.status
                )
              : '',
            time: lastLoadedAt ? formatClock(lastLoadedAt) : t('notLoaded'),
            onRetry: retry,
            onCopyDetails: copyErrorDetails
          }}
          counts={counts}
          allCount={allCount}
          subline={subline}
          query={query}
          selection={selection}
          onSelectionChange={handlePhoneSelection}
          actions={actions}
        />
        {dialogView}
        {toastView}
      </>
    );
  }

  return (
    <AdminPanelShell onOpenMenu={onOpenMenu}>
      <Text
        testID="tenantBreadcrumb"
        style={{
          fontFamily: fontFamily.font500,
          fontSize: 13,
          color: textMutedColor
        }}
      >
        {`${t('breadcrumbRoot')} / ${t('title')}`}
      </Text>
      <Text
        accessibilityRole="header"
        style={{
          fontFamily: fontFamily.font700,
          fontSize: 28,
          color: textColor
        }}
      >
        {t('title')}
      </Text>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 16
        }}
      >
        <Text
          testID="tenantSubline"
          style={{
            fontFamily: fontFamily.font500,
            fontSize: 14,
            color: textMutedColor
          }}
        >
          {subline}
        </Text>
        {counts ? (
          <>
            <Pill
              testID="tenantActivePill"
              style={PILL_GAP}
              text={t('activeCount', {count: counts.active})}
            />
            <Pill
              testID="tenantDeletedPill"
              style={PILL_GAP}
              text={t('deletedCount', {count: counts.deleted})}
            />
          </>
        ) : null}
      </View>

      <GridToolbar
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onClearSearch={clearSearch}
        status={status}
        onStatusChange={setStatus}
        allSorting={allSorting}
        onAllSortingChange={setAllSorting}
        allCount={allCount}
        loading={loading}
        selectedCount={selection.count}
        actions={actions}
        columnLabels={labels}
        hiddenColumns={hiddenColumns}
        onToggleColumn={toggleColumn}
        columnsReady={columnPrefsStatus !== 'pending'}
      />

      {failure ? (
        <>
          <FailureBanner onRetry={retry} />
          <FailurePanel
            statusText={String(
              failure.status === null ? failure.message : failure.status
            )}
            time={lastLoadedAt ? formatClock(lastLoadedAt) : t('notLoaded')}
            onRetry={retry}
            onCopyDetails={copyErrorDetails}
          />
        </>
      ) : null}

      <View
        style={failure ? {display: 'none'} : {flex: 1, position: 'relative'}}
      >
        <ServerPaginatedDataGrid
          columns={columns}
          fetchData={fetchDesktopPage}
          idProperty="id"
          gridId={TENANT_GRID_ID}
          userInfo={prefsUser}
          hideToolbar
          externalSearch={debouncedValue}
          resetKey={`${status}|${allSorting}`}
          refreshKey={refreshKey}
          onFetchError={handleFetchError}
          emptyText={emptyContent}
          onSelectedRowsChange={handleRows}
          gridProps={{
            pageSizes: ROWS_PER_PAGE,
            renderPaginationToolbar: renderPager,
            showColumnMenuTool: false,
            skip: gridSkip,
            onSkipChange: handleSkipChange,
            rowStyle,
            loadingText: t('loading'),
            ...gridProps
          }}
        />
        {loading && !rowsOnScreen ? (
          <View
            testID="tenantSkeletonOverlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 50,
              justifyContent: 'space-between'
            }}
          >
            <ShimmerRows testID="tenantSkeleton" count={8} />
            <PagerPlaceholder />
          </View>
        ) : null}
      </View>

      {dialogView}
      {toastView}
    </AdminPanelShell>
  );
};

export default TenantGrid;
